import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useInventoryManagement } from '../../lib/inventory-hooks';
import { Skeleton } from '../../components/ui/Skeleton';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Package, AlertTriangle, ArrowDownRight, ArrowUpRight, Search, Plus, Edit2, PackagePlus, Trash2 } from 'lucide-react';
import { formatRelativeTime, cn } from '../../lib/utils';
import { InventoryItemModal } from '../../components/admin/InventoryItemModal';
import { StockAdjustmentModal } from '../../components/admin/StockAdjustmentModal';
import type { VInventoryDashboard } from '../../lib/types';
export function InventoryPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const {
    inventory,
    alerts,
    movements,
    loading: invLoading,
    createItem,
    updateItem,
    deleteItem,
    adjustStock,
    toggleItem
  } = useInventoryManagement(restaurant?.id);
  const [filter, setFilter] = useState<'all' | 'low' | 'out'>('all');
  const [search, setSearch] = useState('');
  // Modal state
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<VInventoryDashboard | null>(null);
  const [isAdjustModalOpen, setIsAdjustModalOpen] = useState(false);
  const [adjustingItem, setAdjustingItem] = useState<VInventoryDashboard | null>(null);
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-4676" data-mp="1kiygkb3bln93">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-4677" data-mp="1aroks8dd3ple" />
      </div>;
  }
  const criticalCount = alerts.filter(a => a.alert_level === 'CRITICAL').length;
  const lowCount = alerts.filter(a => a.alert_level === 'LOW').length;
  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.ingredient_name.toLowerCase().includes(search.toLowerCase());
    if (!matchesSearch) return false;
    if (filter === 'out') return item.stock_status === 'out';
    if (filter === 'low') return item.stock_status === 'low' || item.stock_status === 'out';
    return true;
  });
  const handleOpenCreate = () => {
    setEditingItem(null);
    setIsItemModalOpen(true);
  };
  const handleOpenEdit = (item: VInventoryDashboard) => {
    setEditingItem(item);
    setIsItemModalOpen(true);
  };
  const handleOpenAdjust = (item: VInventoryDashboard) => {
    setAdjustingItem(item);
    setIsAdjustModalOpen(true);
  };
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-4678" data-mp="5tt7rsr7po2r">
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6" data-id="element-4679" data-mp="u6zlxq8qy0t">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-4680" data-mp="emerpc6ky0t6">
          <div data-id="element-4681" data-mp="albdsv83eeul">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-4682" data-mp="akt7t6vmom9h">Inventario</h1>
            <p className="text-gray-500" data-id="element-4683" data-mp="3j0l9nlle2ntx">Control de stock e ingredientes</p>
          </div>
          <Button onClick={handleOpenCreate} data-id="element-4684" data-mp="3e1rvdqya2ssp">
            <Plus className="w-4 h-4 mr-2" data-id="element-4685" data-mp="3e339okpcniy2" />
            Nuevo Ingrediente
          </Button>
        </div>

        {/* Alerts Summary */}
        {(criticalCount > 0 || lowCount > 0) && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4686" data-mp="3n1ewebn3hwl8">
            {criticalCount > 0 && <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-4" data-id="element-4687" data-mp="17aahry1n8ugz">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center shrink-0" data-id="element-4688" data-mp="12bfqs8tivzls">
                  <AlertTriangle className="w-6 h-6 text-red-600" data-id="element-4689" data-mp="1xnytyzwaqm5h" />
                </div>
                <div data-id="element-4690" data-mp="22ma3tamlcwnp">
                  <h3 className="font-bold text-red-900" data-id="element-4691" data-mp="22lqsicrg56in">Stock Crítico</h3>
                  <p className="text-red-700 text-sm" data-id="element-4692" data-mp="27jsep6k65lyc">
                    {criticalCount} ingredientes requieren atención inmediata
                  </p>
                </div>
              </div>}
            {lowCount > 0 && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-4" data-id="element-4693" data-mp="2gjfdrlpfefo8">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center shrink-0" data-id="element-4694" data-mp="2qb2zd5zax2hs">
                  <Package className="w-6 h-6 text-amber-600" data-id="element-4695" data-mp="2vapexm2lqutr" />
                </div>
                <div data-id="element-4696" data-mp="30994rxkbgqre">
                  <h3 className="font-bold text-amber-900" data-id="element-4697" data-mp="30a20m3hqlvur">Stock Bajo</h3>
                  <p className="text-amber-700 text-sm" data-id="element-4698" data-mp="34aubt2a66ato">
                    {lowCount} ingredientes por debajo del mínimo sugerido
                  </p>
                </div>
              </div>}
          </div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4699" data-mp="olnnxjlmt7p1">
          {/* Main Inventory Table */}
          <Card className="lg:col-span-2" data-id="element-4700" data-mp="jnvo83eqwhaa">
            <CardHeader className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4" data-id="element-4701" data-mp="jox2shuyzlwr">
              <CardTitle data-id="element-4702" data-mp="1cbhzlxu7sq7p">Ingredientes</CardTitle>

              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto" data-id="element-4703" data-mp="1ca6qdii02q97">
                <div className="relative" data-id="element-4704" data-mp="17dg76mawfejk">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-4705" data-mp="17ehubp56vrhn" />
                  <input type="text" placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber w-full" data-id="element-4706" data-mp="12jc0831mv6ih" />
                </div>
                <div className="flex bg-gray-100 p-1 rounded-lg" data-id="element-4707" data-mp="3kl7q5xmcfnri">
                  <button onClick={() => setFilter('all')} className={cn('px-3 py-1.5 text-sm font-medium rounded-md transition-colors', filter === 'all' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500')} data-id="element-4708" data-mp="3ojex60r09api">
                    Todos
                  </button>
                  <button onClick={() => setFilter('low')} className={cn('px-3 py-1.5 text-sm font-medium rounded-md transition-colors', filter === 'low' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500')} data-id="element-4709" data-mp="35ruhfzbz0wti">
                    Bajos
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent data-id="element-4710" data-mp="153q48eeoklkw">
              {invLoading ? <div className="space-y-4" data-id="element-4711" data-mp="108aspt45b1rn">
                  {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-12 w-full" data-id="element-4712" data-mp="1dzfvv56q2c6q" />)}
                </div> : <div className="overflow-x-auto" data-id="element-4713" data-mp="196ei50i94ioq">
                  <table className="w-full text-sm text-left" data-id="element-4714" data-mp="1nw2gbsv4z188">
                    <thead className="text-xs text-gray-500 uppercase bg-gray-50" data-id="element-4715" data-mp="1nyexi3t2l7k8">
                      <tr data-id="element-4716" data-mp="1ix95y9b5bqca">
                        <th className="px-4 py-3 rounded-l-lg" data-id="element-4717" data-mp="1wxa25bggu9lf">Ingrediente</th>
                        <th className="px-4 py-3 text-right" data-id="element-4718" data-mp="1wv60l061hmob">Stock Actual</th>
                        <th className="px-4 py-3 text-right" data-id="element-4719" data-mp="1sw6nute2glmb">Mínimo</th>
                        <th className="px-4 py-3" data-id="element-4720" data-mp="2ihd5va38h2g">Estado</th>
                        <th className="px-4 py-3 text-right rounded-r-lg" data-id="element-4721" data-mp="3un1n5v7hf2xs">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-4722" data-mp="n0riofenmqh0">
                      {filteredInventory.map(item => <tr key={item.inventory_item_id} className="border-b border-gray-50 last:border-0" data-id="element-4723" data-mp="rzkn7ibyr751">
                          <td className="px-4 py-3 font-medium text-gray-900" data-id="element-4724" data-mp="10th34fejbnc0">
                            {item.ingredient_name}
                            {item.supplier && <div className="text-xs text-gray-400 font-normal" data-id="element-4725" data-mp="93m5lvgiwrc9">
                                {item.supplier}
                              </div>}
                          </td>
                          <td className="px-4 py-3 text-right font-bold" data-id="element-4726" data-mp="i4sqegq7gobb">
                            {item.current_stock}{' '}
                            <span className="text-gray-500 font-normal" data-id="element-4727" data-mp="1tio2thpq6iyu">
                              {item.unit}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500" data-id="element-4728" data-mp="23opgqaoqa159">
                            {item.minimum_stock} {item.unit}
                          </td>
                          <td className="px-4 py-3" data-id="element-4729" data-mp="1tvguqk5ux15x">
                            {item.stock_status === 'ok' ? <Badge variant="success" data-id="element-4730" data-mp="1puy4uvniy56d">OK</Badge> : item.stock_status === 'low' ? <Badge variant="warning" data-id="element-4731" data-mp="1fx0g871r66d8">Bajo</Badge> : <Badge variant="danger" data-id="element-4732" data-mp="1axxi76k6x0er">Agotado</Badge>}
                          </td>
                          <td className="px-4 py-3" data-id="element-4733" data-mp="16x3zcrllubsq">
                            <div className="flex items-center justify-end gap-1" data-id="element-4734" data-mp="16yorkakg5inf">
                              <button onClick={() => handleOpenAdjust(item)} className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" title="Ajustar stock" data-id="element-4735" data-mp="35b5ihdqxq47s">
                                <PackagePlus className="w-4 h-4" data-id="element-4736" data-mp="1hzqdlfql8ea2" />
                              </button>
                              <button onClick={() => handleOpenEdit(item)} className="p-1.5 text-gray-400 hover:text-brand-amber hover:bg-amber-50 rounded-lg transition-colors" title="Editar ingrediente" data-id="element-4737" data-mp="1d0wwzeg0875o">
                                <Edit2 className="w-4 h-4" data-id="element-4738" data-mp="z1xg4syqgwk2" />
                              </button>
                            </div>
                          </td>
                        </tr>)}
                      {filteredInventory.length === 0 && <tr data-id="element-4739" data-mp="1m0762bzxdx9">
                          <td colSpan={5} className="px-4 py-8 text-center text-gray-500" data-id="element-4740" data-mp="1kp2m0aq8253">
                            No se encontraron ingredientes
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>}
            </CardContent>
          </Card>

          {/* Recent Movements */}
          <Card data-id="element-4741" data-mp="2vz391kdmatkk">
            <CardHeader data-id="element-4742" data-mp="2w0mox9vd32lt">
              <CardTitle data-id="element-4743" data-mp="2vx8lrzevipdb">Movimientos Recientes</CardTitle>
            </CardHeader>
            <CardContent data-id="element-4744" data-mp="30xwdknaxfa8l">
              {invLoading ? <Skeleton className="h-64 w-full" data-id="element-4745" data-mp="2mz6ex38gl2i4" /> : movements.length > 0 ? <div className="space-y-4" data-id="element-4746" data-mp="2rx884gzlmi7b">
                  {movements.map(mov => {
                const item = inventory.find(i => i.inventory_item_id === mov.inventory_item_id);
                const isIncrease = mov.movement_type === 'restock';
                return <div key={mov.id} className="flex items-start gap-3 pb-4 border-b border-gray-50 last:border-0 last:pb-0" data-id="element-4747" data-mp="3i78o540pjt72">
                        <div className={cn('w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5', isIncrease ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600')} data-id="element-4748" data-mp="38ge1vzngotlm">
                          {isIncrease ? <ArrowUpRight className="w-4 h-4" data-id="element-4749" data-mp="2pkw83r0piecl" /> : <ArrowDownRight className="w-4 h-4" data-id="element-4750" data-mp="2ljua1dij08c7" />}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-4751" data-mp="jqe8hlkwh92g">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-4752" data-mp="jqwe38tgczn1">
                            {item?.ingredient_name || 'Item desconocido'}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-4753" data-mp="26l9jbpsntz21">
                            {mov.movement_type === 'sale_consumption' ? 'Consumo por venta' : mov.movement_type === 'restock' ? 'Reabastecimiento' : mov.movement_type === 'waste' ? 'Desperdicio' : 'Ajuste manual'}
                            {mov.reason && ` · ${mov.reason}`}
                          </p>
                        </div>
                        <div className={cn('text-sm font-bold shrink-0', isIncrease ? 'text-emerald-600' : 'text-red-600')} data-id="element-4754" data-mp="1wq7xb2told3o">
                          {isIncrease ? '+' : '-'}
                          {Math.abs(mov.quantity)} {item?.unit}
                        </div>
                      </div>;
              })}
                </div> : <p className="text-sm text-gray-500 text-center py-4" data-id="element-4755" data-mp="3fonqe6ud2wc4">
                  No hay movimientos recientes
                </p>}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Item Create/Edit Modal */}
      <InventoryItemModal isOpen={isItemModalOpen} onClose={() => setIsItemModalOpen(false)} item={editingItem} onSave={async data => {
      let result;
      if (editingItem) {
        result = await updateItem(editingItem.inventory_item_id, data);
      } else {
        result = await createItem({
          ...data,
          restaurant_id: restaurant.id
        });
      }
      if (result?.success) {
        setIsItemModalOpen(false);
      }
      return result;
    }} onDelete={editingItem ? async () => {
      if (confirm('¿Estás seguro de eliminar este ingrediente? Se perderán todos los movimientos asociados.')) {
        const result = await deleteItem(editingItem.inventory_item_id);
        if (result?.success) {
          setIsItemModalOpen(false);
        }
        return result;
      }
      return {
        success: false
      };
    } : undefined} data-id="element-4756" data-mp="3kn7diakoye62" />

      {/* Stock Adjustment Modal */}
      <StockAdjustmentModal isOpen={isAdjustModalOpen} onClose={() => setIsAdjustModalOpen(false)} item={adjustingItem} onAdjust={adjustStock} data-id="element-4757" data-mp="31np7c2bqdnf" />
    </AdminLayout>;
}